/** Admin · releases & versions (GET /admin/releases) — what is deployed where: version, region, cell, migrations. */
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api.js";

interface ReleasePayload {
  version: string;
  region: string;
  cell: string;
  node: string;
  migrations: { name: string; appliedAt: string }[];
}

interface ReleaseResponse {
  release: ReleasePayload;
}

export function Releases(): ReactNode {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-releases"],
    queryFn: () => api.get<ReleaseResponse>("/admin/releases"),
  });
  const r = data?.release;

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Releases & versions</h1>
          <div className="sub">What is deployed where — with dates · live from the running API</div>
        </div>
        <span className="pill n">CI/CD · prod is a gated promotion</span>
      </div>

      {isLoading && <div className="card pad mut sm">Loading release info…</div>}

      {isError && (
        <div className="banner crit">
          <span>Couldn't load release info. Please retry in a moment.</span>
        </div>
      )}

      {r && (
        <>
          <div className="grid g4" style={{ marginBottom: 16 }}>
            <div className="card pad stat">
              <div className="l">Current build</div>
              <div className="n violet tnum">{r.version}</div>
              <div className="d">reported by the running API</div>
            </div>
            <div className="card pad stat">
              <div className="l">Region</div>
              <div className="n tnum">{r.region}</div>
              <div className="d">residency boundary</div>
            </div>
            <div className="card pad stat">
              <div className="l">Cell</div>
              <div className="n tnum">{r.cell}</div>
              <div className="d">pod shard within the region</div>
            </div>
            <div className="card pad stat">
              <div className="l">Node runtime</div>
              <div className="n teal tnum">{r.node}</div>
              <div className="d">Functions host</div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-h">
              <h3>Applied migrations</h3>
              <span className="tag">Schema</span>
            </div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Migration</th>
                    <th>Applied</th>
                  </tr>
                </thead>
                <tbody>
                  {r.migrations.length === 0 && (
                    <tr>
                      <td colSpan={2} className="mut sm">
                        No migrations recorded.
                      </td>
                    </tr>
                  )}
                  {r.migrations.map((m) => (
                    <tr key={m.name}>
                      <td style={{ fontFamily: "var(--mono)", fontSize: 12.5 }}>{m.name}</td>
                      <td className="mut sm">{new Date(m.appliedAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <div className="disclaimer">
        <b>Prod is a gated promotion, never an auto-deploy:</b> a named commit is dispatched by hand, schema
        changes pause on a human approval, and a bad release is undone by redeploying the previous tag.
      </div>
    </section>
  );
}
